import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useState } from "react";
import { ClipboardCheck, Loader2, Plus, Mail } from "lucide-react";

const SAMPLE = `Portable laptop computer 14 inch 1.2 kg | 8471300000
Wireless bluetooth headphones | 8517620000
Men's cotton knitted t-shirts | 6109100010
Steel threaded screws and bolts | 7318159000
Office wooden desk | 9403300000`;

function parseLines(text) {
  return text.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const sep = l.lastIndexOf("|");
    if (sep === -1) return { description: l, declared_hs_code: "" };
    return { description: l.slice(0, sep).trim(), declared_hs_code: l.slice(sep + 1).trim() };
  });
}

export default function HealthChecks() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [clientName, setClientName] = useState("ACME Trading GmbH");
  const [clientEmail, setClientEmail] = useState("compliance@acme.example");
  const [text, setText] = useState(SAMPLE);

  const { data = [], isLoading } = useQuery({
    queryKey: ["healthchecks"],
    queryFn: async () => (await api.get("/health-checks")).data,
    refetchInterval: 8000,
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/health-checks", {
      client_name: clientName, client_email: clientEmail, lines: parseLines(text), run: true,
    })).data,
    onSuccess: (hc) => { toast.success(`${hc.hc_id} analyzed`); setOpen(false); qc.invalidateQueries({ queryKey: ["healthchecks"] }); navigate(`/health-checks/${hc.hc_id}`); },
    onError: () => toast.error("Failed to create health check"),
  });

  return (
    <div className="animate-fade-in">
      <header className="h-14 border-b border-zinc-800 flex items-center px-6 gap-4 sticky top-0 bg-background z-10">
        <h1 className="font-heading text-lg font-semibold tracking-tight">HS Classification Health Checks</h1>
        <span className="text-xs font-mono text-zinc-500">{data.length}</span>
        <button data-testid="new-hc-btn" onClick={() => setOpen(true)}
          className="ml-auto flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm px-3 py-2 rounded-sm transition-colors duration-150">
          <Plus className="h-4 w-4" /> New health check
        </button>
      </header>

      <div className="p-6">
        <div className="border border-zinc-800 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-950 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="text-left font-medium py-2 px-3">ID</th>
                <th className="text-left font-medium py-2 px-3">Client</th>
                <th className="text-left font-medium py-2 px-3">Source</th>
                <th className="text-left font-medium py-2 px-3">Lines</th>
                <th className="text-left font-medium py-2 px-3">Findings</th>
                <th className="text-left font-medium py-2 px-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="py-10 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
              {!isLoading && data.length === 0 && (
                <tr><td colSpan={6} className="py-16 text-center text-zinc-500">
                  <ClipboardCheck className="h-8 w-8 mx-auto mb-3 text-zinc-700" />
                  <p className="text-sm">No health checks yet. Create one or send an <span className="font-mono text-zinc-400">@ROKET healthcheck</span> email.</p>
                </td></tr>
              )}
              {data.map((h) => (
                <tr key={h.hc_id} data-testid={`hc-row-${h.hc_id}`} onClick={() => navigate(`/health-checks/${h.hc_id}`)}
                  className="border-t border-zinc-800/60 hover:bg-zinc-900 cursor-pointer transition-colors duration-150">
                  <td className="py-2 px-3 font-mono text-blue-400">{h.hc_id}</td>
                  <td className="py-2 px-3 text-zinc-300">{h.client_name || "—"}</td>
                  <td className="py-2 px-3 font-mono text-zinc-500 text-xs flex items-center gap-1">{h.source === "email" && <Mail className="h-3 w-3" />}{h.source}</td>
                  <td className="py-2 px-3 font-mono text-zinc-300">{h.summary?.total ?? h.lines?.length ?? 0}</td>
                  <td className="py-2 px-3 text-xs font-mono">
                    <span className="text-emerald-400">{h.summary?.valid ?? 0}✓</span>{" "}
                    <span className="text-amber-400">{h.summary?.review ?? 0}?</span>{" "}
                    <span className="text-red-400">{h.summary?.incorrect ?? 0}✗</span>
                  </td>
                  <td className="py-2 px-3"><HcStatus status={h.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-zinc-950/80 p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-lg bg-zinc-900 border border-zinc-800 rounded-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-semibold mb-1">New HS Classification Health Check</h3>
            <p className="text-xs text-zinc-500 mb-4">One product per line — <span className="font-mono">description | declared HS code</span>. Roket validates each line.</p>
            <div className="flex gap-3 mb-3">
              <div className="flex-1">
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Client name</label>
                <input data-testid="hc-client-name" value={clientName} onChange={(e) => setClientName(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none" />
              </div>
              <div className="flex-1">
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Client email</label>
                <input data-testid="hc-client-email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono focus:border-blue-500 outline-none" />
              </div>
            </div>
            <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Classification lines</label>
            <textarea data-testid="hc-lines" rows={7} value={text} onChange={(e) => setText(e.target.value)}
              className="w-full mb-4 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-xs font-mono focus:border-blue-500 outline-none resize-none" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="text-sm px-3 py-2 border border-zinc-700 rounded-sm hover:bg-zinc-800">Cancel</button>
              <button data-testid="hc-run" disabled={create.isPending} onClick={() => create.mutate()}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white text-sm px-4 py-2 rounded-sm">
                {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Analyze
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HcStatus({ status }) {
  const map = {
    created: "border-zinc-700 text-zinc-400",
    analyzing: "border-amber-500/40 text-amber-400 animate-pulse",
    review_required: "border-blue-500/40 text-blue-400",
    approved: "border-emerald-500/40 text-emerald-400",
    sent: "border-emerald-600/50 text-emerald-300",
    send_failed: "border-red-500/50 text-red-400",
  };
  return <span className={`inline-flex px-2 py-0.5 rounded-sm border text-[10px] font-mono tracking-wider ${map[status] || "border-zinc-700 text-zinc-400"}`}>{(status || "—").toUpperCase()}</span>;
}
